import { func, bool, object } from 'prop-types'
import { PureComponent } from 'react'
import {
  Button,
  kuiperClassNames,
  Conditional,
  ConnectedAvatar,
  PopoverV2,
  Icon,
} from '@opallabs/kuiper'
import {
  renderAvatarCompletedDialogue,
  renderAvatarCompletedByProxyDialogue,
  renderIntegrationCompletedByProxyDialogue,
  renderIntegrationCompletedDialogue,
} from './dialogues'
import './styles.scss'

const taskIsComplete = (task) => task.checked && task.completedAt

const taskWasCompletedByProxyUser = (assignee, proxyUser) => {
  const toUserIsProxy =
    (proxyUser && proxyUser.id) === (assignee && assignee.id)

  return proxyUser && !toUserIsProxy
}

const taskIsAnIntegrationTask = (task) => task.integration_task

const integrationTaskWasCompletedByProxyUser = (proxyUser) => proxyUser

const integrationTaskWasCompletedViaIntegration = (assignee) =>
  assignee.fullName.trim() === 'Workfront'

class AssignedUserAvatar extends PureComponent {
  static propTypes = {
    assignee: object,
    content: object,
    createIntegrationTask: func,
    disabled: bool,
    loadIntegrationTask: func,
    onBlur: func,
    onFocus: func,
    onUnassignUser: func,
    proxyUser: object,
    task: object,
  }

  static defaultProps = {
    onUnassignUser: () => {},
  }

  constructor(props) {
    super(props)
    this.state = {
      forceClosePopover: false,
      hasIntegrationError: false,
      isUsingKeyboard: false,
    }
  }

  static getDerivedStateFromProps(props, state) {
    const task = props.task
    let hasIntegrationError = false

    if (task.integrationTask && task.integrationTask.provider_error) {
      hasIntegrationError = true
    }

    return {
      ...state,
      hasIntegrationError,
    }
  }

  handleKeyPress = (e) => {
    e.stopPropagation()
    switch (e.which) {
      case 13:
        this.onEnterPressed()
        break
      case 27:
        this.onEscapePressed()
        break
      default:
        break
    }
  }

  onEnterPressed() {
    this.setState(
      () => ({ isUsingKeyboard: true }),
      () => {
        this.trigger.click()
      }
    )
  }

  onEscapePressed() {
    this.props.onFocus(this.state.isUsingKeyboard)
    if (this.state.isUsingKeyboard) {
      this.trigger.focus()
    }
    this.setState({ forceClosePopover: true })
  }

  onAfterPopoverOpen() {
    const button = this.content.querySelector('.kuiper-button')
    if (button) button.focus()
  }

  onBeforePopoverClose = () => {
    this.setState({ isUsingKeyboard: false })
  }

  onUnassignUser = () => {
    this.props.onFocus(this.state.isUsingKeyboard)
    const event = this.state.isUsingKeyboard ? 'keyPress' : 'click'
    this.setState(
      () => ({ forceClosePopover: true }),
      () => {
        this.props.onUnassignUser(event)
      }
    )
  }

  onClickRedeliverTask = () => {
    // We have an integration task record in Opal,
    // but something went wrong in transit to the provider.
    // Allow the user to attempt redelivery.
    // When we're attempting to redeliver,
    // it's always to the same integration and project that was initially selected by the user.
    const integrationTask = this.props.task.integrationTask

    // Update this object with `null` provider_error.
    // This causes the UI to update to the state we want,
    // where "sending to provider" displays and the "redeliver"
    // button can't be spammed.
    const data = {
      data: {
        id: integrationTask.id,
        type: 'integration-task',
        attributes: {
          provider_error: null,
          provider_url: integrationTask.provider_url,
        },
      },
    }
    this.props.loadIntegrationTask(data)

    // Attempt redelivery.
    this.props.createIntegrationTask({
      taskId: this.props.task.id,
      workspaceIntegrationUuid: integrationTask.workspace_integration_uuid,
      projectUuid: integrationTask.project_uuid,
      contentId: this.props.content.id,
      callback: (getIntegrationTask) => {
        // After the integration task is created, a delayed job
        // will create the task in the provider via Opal IO.
        // We re-fetch the task here hoping it has been updated
        // with a provider URL to display in the UI.
        setTimeout(() => {
          getIntegrationTask()
        }, 10000)
      },
    })

    this.setState({ forceClosePopover: true })
  }

  onClickTrigger = () => {
    this.setState({ isUsingKeyboard: false })
  }

  popoverContent = () => {
    const { assignee, proxyUser, task } = this.props

    if (taskIsComplete(task)) {
      if (taskWasCompletedByProxyUser(assignee, proxyUser)) {
        return renderAvatarCompletedByProxyDialogue(
          assignee,
          proxyUser,
          task.completedAt
        )
      }

      if (taskIsAnIntegrationTask(task)) {
        if (integrationTaskWasCompletedByProxyUser(proxyUser)) {
          return renderIntegrationCompletedByProxyDialogue(
            assignee,
            'Workfront',
            task.completedAt
          )
        }

        if (integrationTaskWasCompletedViaIntegration(assignee)) {
          return renderIntegrationCompletedDialogue(
            assignee.fullName,
            task.completedAt
          )
        }
      }

      // Task was completed by tasks to_user
      return renderAvatarCompletedDialogue(assignee, task.completedAt)
    }

    if (this.state.hasIntegrationError) {
      return this.renderIntegrationTaskErrorDialogue()
    }

    return this.renderAvatarDialogue()
  }

  popoverTrigger() {
    const hasIntegrationError = this.state.hasIntegrationError

    const { assignee } = this.props
    const disabledClass = this.props.disabled ? 'disabled' : ''
    const errorClass = hasIntegrationError ? 'integration-error' : ''

    /* eslint-disable jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-static-element-interactions */
    return (
      <div className={kuiperClassNames('assignedUser', disabledClass)}>
        <div
          className={kuiperClassNames('avatar-wrapper', errorClass)}
          ref={(trigger) => (this.trigger = trigger)}
          onBlur={this.props.onBlur}
          onMouseDown={this.onClickTrigger}
          onKeyDown={this.handleKeyPress}
          tabIndex={0}
        >
          <Conditional condition={hasIntegrationError}>
            <Icon
              glyph="url"
              style={{ color: '#FFFFFF', fill: '#FFFFFF' }}
              height={14}
              width={14}
              ariaHidden
            />
          </Conditional>
          <Conditional condition={!hasIntegrationError}>
            <ConnectedAvatar size={20} userId={assignee.id} />
          </Conditional>
        </div>
      </div>
    )
    /* eslint-enable jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-static-element-interactions */
  }

  renderAvatarDialogue() {
    let fullName = this.props.assignee.fullName
    if (!this.props.assignee.is_active) {
      fullName = `${fullName} (Inactive)`
    }
    return (
      <div className={kuiperClassNames('phase-popover', 'n10')}>
        <div className={kuiperClassNames('popover-text')}>
          <div>This task is assigned to</div>
          <div>{fullName}</div>
        </div>
        {this.renderUnassignButton()}
      </div>
    )
  }

  renderUnassignButton() {
    if (this.props.task.lockedTask) return null
    const className = this.state.isUsingKeyboard ? 'keyboard-focus' : ''

    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div onKeyDown={this.handleKeyPress}>
        <div className={kuiperClassNames('popover-hr')} />
        <Button
          className={kuiperClassNames(className)}
          label="Unassign this task"
          type="invisible"
          color="blue"
          onClick={this.onUnassignUser}
        />
      </div>
    )
  }

  getRedeliverLabelText() {
    const task = this.props.task
    const taskWorskpaceIntegrationUuid =
      task.integrationTask.workspace_integration_uuid
    const enabledIntegrations = task.enabledIntegrations
    const providerName =
      Object.keys(enabledIntegrations).find(
        (key) =>
          enabledIntegrations[key].workspaceIntegrationUuid ===
          taskWorskpaceIntegrationUuid
      ) || 'integration'

    return `Retry Sending to ${providerName}`
  }

  renderRedeliverIntegrationTaskButton() {
    if (this.props.task.lockedTask) return null
    const className = this.state.isUsingKeyboard ? 'keyboard-focus' : ''

    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div onKeyDown={this.handleKeyPress}>
        <div className={kuiperClassNames('popover-hr')} />
        <Button
          className={kuiperClassNames(className)}
          label={this.getRedeliverLabelText()}
          type="invisible"
          color="blue"
          onClick={this.onClickRedeliverTask}
        />
      </div>
    )
  }

  renderIntegrationTaskErrorDialogue() {
    return (
      <div className={kuiperClassNames('phase-popover', 'n10')}>
        <div className={kuiperClassNames('popover-text')}>
          <div>
            There was an error delivering this task. Please try resending it.
          </div>
        </div>
        {this.renderRedeliverIntegrationTaskButton()}
      </div>
    )
  }

  render() {
    if (this.props.disabled) {
      return this.popoverTrigger()
    }

    return (
      <div className={kuiperClassNames('assignedUser')}>
        <PopoverV2
          className={kuiperClassNames('assignedUserNotice', 'no-padding')}
          content={this.popoverContent()}
          forceClose={this.state.forceClosePopover}
          onClose={this.onBeforePopoverClose}
          onOpened={this.onAfterPopoverOpen}
          trigger={this.popoverTrigger()}
        />
      </div>
    )
  }
}

export default AssignedUserAvatar
